import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Building, Percent, Globe, Save, FileText, Mail, Phone, MapPin, Palette } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { formatDZD } from '../utils/currency';
import { useTranslation } from 'react-i18next';
import './Settings.css';

const Settings = () => {
  const { t } = useTranslation();
  const { settings, updateSettings } = useSettings();
  const [activeTab, setActiveTab] = useState('company');
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const wilayas = [
    'Adrar', 'Chlef', 'Laghouat', 'Oum El Bouaghi', 'Batna', 'Béjaïa', 'Biskra', 'Béchar',
    'Blida', 'Bouira', 'Tamanrasset', 'Tébessa', 'Tlemcen', 'Tiaret', 'Tizi Ouzou', 'Alger',
    'Djelfa', 'Jijel', 'Sétif', 'Saïda', 'Skikda', 'Sidi Bel Abbès', 'Annaba', 'Guelma',
    'Constantine', 'Médéa', 'Mostaganem', 'MSila', 'Mascara', 'Ouargla', 'Oran', 'El Bayadh',
    'Illizi', 'Bordj Bou Arréridj', 'Boumerdès', 'El Tarf', 'Tindouf', 'Tissemsilt', 'El Oued',
    'Khenchela', 'Souk Ahras', 'Tipaza', 'Mila', 'Aïn Defla', 'Naâma', 'Aïn Témouchent',
    'Ghardaïa', 'Relizane', 'Timimoun', 'Bordj Badji Mokhtar', 'Ouled Djellal', 'Béni Abbès',
    'In Salah', 'In Guezzam', 'Touggourt', 'Djanet', 'El MGhair', 'El Meniaa'
  ];

  const banks = [
    { code: 'BNA', name: 'Banque Nationale d\'Algérie' },
    { code: 'BEA', name: 'Banque Extérieure d\'Algérie' },
    { code: 'CPA', name: 'Crédit Populaire d\'Algérie' },
    { code: 'BADR', name: 'Banque de l\'Agriculture et du Développement Rural' },
    { code: 'BDL', name: 'Banque de Développement Local' },
    { code: 'CNEP', name: 'Caisse Nationale d\'Épargne et de Prévoyance' },
    { code: 'BHA', name: 'Banque de l\'Habitat d\'Algérie' },
    { code: 'BNAH', name: 'Banque Nationale de l\'Habitat' },
    { code: 'BIM', name: 'Banque de l\'Industrie et des Mines' },
    { code: 'SGA', name: 'Société Générale Algérie' },
    { code: 'BNPP', name: 'BNP Paribas El Djazair' },
    { code: 'ABALG', name: 'Arab Bank PLC Algeria' },
    { code: 'TBA', name: 'Trust Bank Algeria' },
    { code: 'AGB', name: 'Gulf Bank Algeria' },
    { code: 'FBD', name: 'Fransabank El Djazair' },
    { code: 'BAA', name: 'Banque Al Baraka d\'Algérie' },
    { code: 'ASBA', name: 'Al Salam Bank Algeria' },
    { code: 'BZA', name: 'Banque Zitouna Algérie' },
    { code: 'ABC', name: 'Arab Banking Corporation Algérie' },
    { code: 'NATIXIS', name: 'Natixis Algérie' },
    { code: 'HBTF', name: 'Housing Bank for Trade and Finance' },
    { code: 'CA', name: 'Crédit Agricole Corporate & Investment Bank' },
    { code: 'HSBC', name: 'HSBC Algeria' },
    { code: 'CITI', name: 'Citibank Algeria' },
    { code: 'BPA', name: 'Banque Postale Algérienne' },
    { code: 'BFI', name: 'Banque de Financement Islamique' },
    { code: 'AP', name: 'Algérie Poste (CCP/Edahabia)' },
    { code: 'CNMA', name: 'Caisse Nationale de Mutualité Agricole' }
  ];

  // Fonction pour parser un compte bancaire existant
  const parseBankAccount = (accountString) => {
    if (!accountString) return { account: '', bank: '', wilaya: '' };
    
    // Trouver la banque dans la chaîne
    const bank = banks.find(b => accountString.includes(b.code));
    if (!bank) return { account: accountString, bank: '', wilaya: '' };
    
    // Trouver la wilaya dans la chaîne
    const wilaya = wilayas.find(w => accountString.includes(w));
    
    // Le numéro de compte est tout ce qui précède le code de la banque
    const parts = accountString.split(bank.code);
    const account = parts[0].trim();
    
    return {
      account,
      bank: bank.code,
      wilaya: wilaya || ''
    };
  };

  // État pour gérer les comptes bancaires
  const [bankAccounts, setBankAccounts] = useState([
    parseBankAccount(settings.company.bankAccount1),
    parseBankAccount(settings.company.bankAccount2),
    parseBankAccount(settings.company.bankAccount3)
  ]);

  // Mettre à jour les comptes bancaires quand les paramètres changent
  useEffect(() => {
    setBankAccounts([
      parseBankAccount(settings.company.bankAccount1),
      parseBankAccount(settings.company.bankAccount2),
      parseBankAccount(settings.company.bankAccount3)
    ]);
  }, [settings.company.bankAccount1, settings.company.bankAccount2, settings.company.bankAccount3]);

  // Fonction pour mettre à jour un compte bancaire
  const handleBankAccountChange = (index, field, value) => {
    const newAccounts = [...bankAccounts];
    newAccounts[index] = { ...newAccounts[index], [field]: value };
    setBankAccounts(newAccounts);

    // Mettre à jour les paramètres de l'entreprise
    const formatAccountString = (accountObj) => {
      if (!accountObj.account) return '';
      return `${accountObj.account} ${accountObj.bank}${accountObj.wilaya ? ' ' + accountObj.wilaya : ''}`;
    };

    if (index === 0) {
      updateSettings('company', 'bankAccount1', formatAccountString(newAccounts[0]));
    } else if (index === 1) {
      updateSettings('company', 'bankAccount2', formatAccountString(newAccounts[1]));
    } else if (index === 2) {
      updateSettings('company', 'bankAccount3', formatAccountString(newAccounts[2]));
    }
  };

  const handleSave = () => {
    // Les paramètres sont automatiquement sauvegardés via le contexte
    console.log('Paramètres sauvegardés:', settings);
    setUnsavedChanges(false);
    alert('Paramètres sauvegardés avec succès !');
  };

  const tabs = [
    { id: 'company', label: 'Entreprise', icon: Building },
    { id: 'design', label: 'Design & Personnalisation', icon: Palette },
    { id: 'taxes', label: 'Taxes', icon: Percent },
    { id: 'documents', label: 'Documents & Numérotation', icon: FileText },
    { id: 'notifications', label: 'Notifications', icon: Mail }
  ];

  return (
    <div className="settings fade-in">
      <div className="page-header">
        <h1 className="page-title" style={{ color: '#ffffff' }}>
          <SettingsIcon size={32} />
          {t('settings.title')}
        </h1>
        <p className="page-subtitle" style={{ color: '#ffffff' }}>
          {t('settings.subtitle')}
        </p>
      </div>

      <div className="settings-container">
        {/* Tabs Navigation */}
        <div className="settings-tabs">
          {tabs.map(tab => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                className={`tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <IconComponent size={20} />
                {t(`settings.tabs.${tab.id}`)}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="settings-content">
          {/* Company Settings */}
          {activeTab === 'company' && (
            <div className="settings-section">
              <h2 className="section-title">Informations de l'entreprise</h2>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Nom de l'entreprise (Français) *</label>
                  <input
                    type="text"
                    value={settings.company.name}
                    onChange={(e) => updateSettings('company', 'name', e.target.value)}
                    placeholder="BRIDGE iT"
                  />
                </div>

                <div className="form-group">
                  <label>Logo de l'entreprise</label>
                  <div className="logo-upload-container">
                    {settings.company.logo && (
                      <div className="current-logo">
                        <img 
                          src={settings.company.logo} 
                          alt="Logo de l'entreprise" 
                          className="logo-preview"
                        />
                        <button 
                          type="button" 
                          className="remove-logo" 
                          onClick={() => updateSettings('company', 'logo', null)}
                        >
                          Supprimer
                        </button>
                      </div>
                    )}
                    <label className="logo-upload-button">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              updateSettings('company', 'logo', reader.result);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        style={{ display: 'none' }}
                      />
                      {settings.company.logo ? 'Changer le logo' : 'Uploader un logo'}
                    </label>
                    <p className="logo-tip">Format recommandé: PNG ou SVG transparent, max 1MB</p>
                  </div>
                </div>

                <div className="form-group">
                  <label>2ème nom de l'entreprise (Arabe/Latin - optionnel)</label>
                  <input
                    type="text"
                    className="secondary-name-input"
                    value={settings.company.nameArabic}
                    onChange={(e) => updateSettings('company', 'nameArabic', e.target.value)}
                    placeholder="Ex: بريدج آي تي ou BRIDGE IT"
                    style={{ 
                      direction: /[\u0600-\u06FF]/.test(settings.company.nameArabic || '') ? 'rtl' : 'ltr',
                      textAlign: /[\u0600-\u06FF]/.test(settings.company.nameArabic || '') ? 'right' : 'left'
                    }}
                  />
                </div>

                <div className="form-group full-width">
                  <label>Activité principale *</label>
                  <input
                    type="text"
                    value={settings.company.activity}
                    onChange={(e) => updateSettings('company', 'activity', e.target.value)}
                    placeholder="Développement de logiciels - Solutions informatiques"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Services (Description) *</label>
                  <input
                    type="text"
                    value={settings.company.services}
                    onChange={(e) => updateSettings('company', 'services', e.target.value)}
                    placeholder="(Applications web et mobiles) Systèmes de gestion - Consulting IT"
                  />
                </div>

                <div className="form-group">
                  <label>ARTICLE N° *</label>
                  <input
                    type="text"
                    value={settings.company.articleNumber}
                    onChange={(e) => updateSettings('company', 'articleNumber', e.target.value)}
                    placeholder="1601240105"
                  />
                </div>

                <div className="form-group">
                  <label>NIF N° *</label>
                  <input
                    type="text"
                    value={settings.company.nif}
                    onChange={(e) => updateSettings('company', 'nif', e.target.value)}
                    placeholder="000000041987654"
                  />
                </div>

                <div className="form-group">
                  <label>NIS N° *</label>
                  <input
                    type="text"
                    value={settings.company.nis}
                    onChange={(e) => updateSettings('company', 'nis', e.target.value)}
                    placeholder="000000041987654"
                  />
                </div>

                <div className="form-group">
                  <label>RC N° *</label>
                  <input
                    type="text"
                    value={settings.company.rc}
                    onChange={(e) => updateSettings('company', 'rc', e.target.value)}
                    placeholder="16/00-0024680"
                  />
                </div>

                <div className="form-group">
                  <label>Compte bancaire 1 *</label>
                  <div className="bank-account-inputs">
                    <input
                      type="text"
                      value={bankAccounts[0].account}
                      onChange={(e) => handleBankAccountChange(0, 'account', e.target.value)}
                      placeholder="Numéro de compte"
                      className="account-number"
                    />
                    <select
                      value={bankAccounts[0].bank}
                      onChange={(e) => handleBankAccountChange(0, 'bank', e.target.value)}
                      className="bank-select"
                    >
                      <option value="">Sélectionner une banque</option>
                      {banks.map(bank => (
                        <option key={bank.code} value={bank.code}>{bank.name} ({bank.code})</option>
                      ))}
                    </select>
                    <select
                      value={bankAccounts[0].wilaya}
                      onChange={(e) => handleBankAccountChange(0, 'wilaya', e.target.value)}
                      className="wilaya-select"
                    >
                      <option value="">Sélectionner une wilaya</option>
                      {wilayas.map(wilaya => (
                        <option key={wilaya} value={wilaya}>{wilaya}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Compte bancaire 2</label>
                  <div className="bank-account-inputs">
                    <input
                      type="text"
                      value={bankAccounts[1].account}
                      onChange={(e) => handleBankAccountChange(1, 'account', e.target.value)}
                      placeholder="Numéro de compte"
                      className="account-number"
                    />
                    <select
                      value={bankAccounts[1].bank}
                      onChange={(e) => handleBankAccountChange(1, 'bank', e.target.value)}
                      className="bank-select"
                    >
                      <option value="">Sélectionner une banque</option>
                      {banks.map(bank => (
                        <option key={bank.code} value={bank.code}>{bank.name} ({bank.code})</option>
                      ))}
                    </select>
                    <select
                      value={bankAccounts[1].wilaya}
                      onChange={(e) => handleBankAccountChange(1, 'wilaya', e.target.value)}
                      className="wilaya-select"
                    >
                      <option value="">Sélectionner une wilaya</option>
                      {wilayas.map(wilaya => (
                        <option key={wilaya} value={wilaya}>{wilaya}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Compte bancaire 3</label>
                  <div className="bank-account-inputs">
                    <input
                      type="text"
                      value={bankAccounts[2].account}
                      onChange={(e) => handleBankAccountChange(2, 'account', e.target.value)}
                      placeholder="Numéro de compte"
                      className="account-number"
                    />
                    <select
                      value={bankAccounts[2].bank}
                      onChange={(e) => handleBankAccountChange(2, 'bank', e.target.value)}
                      className="bank-select"
                    >
                      <option value="">Sélectionner une banque</option>
                      {banks.map(bank => (
                        <option key={bank.code} value={bank.code}>{bank.name} ({bank.code})</option>
                      ))}
                    </select>
                    <select
                      value={bankAccounts[2].wilaya}
                      onChange={(e) => handleBankAccountChange(2, 'wilaya', e.target.value)}
                      className="wilaya-select"
                    >
                      <option value="">Sélectionner une wilaya</option>
                      {wilayas.map(wilaya => (
                        <option key={wilaya} value={wilaya}>{wilaya}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group full-width">
                  <label>Adresse *</label>
                  <input
                    type="text"
                    value={settings.company.address}
                    onChange={(e) => updateSettings('company', 'address', e.target.value)}
                    placeholder="Adresse complète"
                  />
                </div>

                <div className="form-group">
                  <label>Ville/Wilaya *</label>
                  <select
                    value={settings.company.city}
                    onChange={(e) => updateSettings('company', 'city', e.target.value)}
                  >
                    <option value="">Sélectionner une wilaya</option>
                    {wilayas.map(wilaya => (
                      <option key={wilaya} value={wilaya}>{wilaya}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Code postal</label>
                  <input
                    type="text"
                    value={settings.company.postalCode}
                    onChange={(e) => updateSettings('company', 'postalCode', e.target.value)}
                    placeholder="16000"
                  />
                </div>

                <div className="form-group">
                  <label>Téléphone *</label>
                  <input
                    type="tel"
                    value={settings.company.phone}
                    onChange={(e) => updateSettings('company', 'phone', e.target.value)}
                    placeholder="+213 21 123 456"
                  />
                </div>

                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={settings.company.email}
                    onChange={(e) => updateSettings('company', 'email', e.target.value)}
                    placeholder="contact@entreprise.dz"
                  />
                </div>

                <div className="form-group">
                  <label>Site web</label>
                  <input
                    type="url"
                    value={settings.company.website}
                    onChange={(e) => updateSettings('company', 'website', e.target.value)}
                    placeholder="www.entreprise.dz"
                  />
                </div>


              </div>
            </div>
          )}

          {/* Design & Customization Settings */}
          {activeTab === 'design' && (
            <div className="settings-section">
              <h2 className="section-title">Design & Personnalisation</h2>
              <p className="section-description">Personnalisez l'apparence de vos documents et en-têtes</p>
              
              {/* Bibliothèque de modèles */}
              <div className="templates-library">
                <h3>Bibliothèque de modèles</h3>
                <p className="library-description">Choisissez un modèle prédéfini pour vos documents</p>
                
                <div className="templates-grid">
                  {/* Modèle Moderne */}
                  <div 
                    className={`template-card ${settings.design.documentTemplate === 'modern-blue' ? 'selected' : ''}`}
                    onClick={() => {
                      updateSettings('design', 'documentTemplate', 'modern-blue');
                      updateSettings('design', 'headerStyle', 'modern');
                      updateSettings('design', 'colorScheme', 'blue');
                      updateSettings('design', 'accentColor', '#1e88e5');
                      updateSettings('design', 'fontFamily', 'inter');
                    }}
                  >
                    <div className="template-preview modern-blue">
                      <div className="template-header">
                        <div className="template-logo blue"></div>
                        <div className="template-info">
                          <div className="template-title blue">ENTERPRISE NAME</div>
                          <div className="template-subtitle">Business Activity</div>
                        </div>
                      </div>
                      <div className="template-content">
                        <div className="template-line"></div>
                        <div className="template-line short"></div>
                        <div className="template-line"></div>
                      </div>
                    </div>
                    <div className="template-details">
                      <h4>Moderne Bleu</h4>
                      <p>Design épuré et moderne avec accents bleus</p>
                    </div>
                  </div>

                  {/* Modèle Classique */}
                  <div 
                    className={`template-card ${settings.design.documentTemplate === 'classic-gray' ? 'selected' : ''}`}
                    onClick={() => {
                      updateSettings('design', 'documentTemplate', 'classic-gray');
                      updateSettings('design', 'headerStyle', 'classic');
                      updateSettings('design', 'colorScheme', 'gray');
                      updateSettings('design', 'accentColor', '#374151');
                      updateSettings('design', 'fontFamily', 'times');
                    }}
                  >
                    <div className="template-preview classic-gray">
                      <div className="template-header">
                        <div className="template-info centered">
                          <div className="template-title gray">ENTERPRISE NAME</div>
                          <div className="template-subtitle">Business Activity</div>
                        </div>
                      </div>
                      <div className="template-separator"></div>
                      <div className="template-content">
                        <div className="template-line"></div>
                        <div className="template-line"></div>
                        <div className="template-line short"></div>
                      </div>
                    </div>
                    <div className="template-details">
                      <h4>Classique Gris</h4>
                      <p>Style traditionnel et professionnel</p>
                    </div>
                  </div>

                  {/* Modèle Élégant */}
                  <div 
                    className={`template-card ${settings.design.documentTemplate === 'elegant-purple' ? 'selected' : ''}`}
                    onClick={() => {
                      updateSettings('design', 'documentTemplate', 'elegant-purple');
                      updateSettings('design', 'headerStyle', 'elegant');
                      updateSettings('design', 'colorScheme', 'purple');
                      updateSettings('design', 'accentColor', '#8b5cf6');
                      updateSettings('design', 'fontFamily', 'helvetica');
                    }}
                  >
                    <div className="template-preview elegant-purple">
                      <div className="template-header gradient">
                        <div className="template-logo purple"></div>
                        <div className="template-info">
                          <div className="template-title purple">ENTERPRISE NAME</div>
                          <div className="template-subtitle">Business Activity</div>
                        </div>
                      </div>
                      <div className="template-content">
                        <div className="template-line"></div>
                        <div className="template-line short"></div>
                        <div className="template-line"></div>
                      </div>
                    </div>
                    <div className="template-details">
                      <h4>Élégant Violet</h4>
                      <p>Design raffiné avec dégradés</p>
                    </div>
                  </div>

                  {/* Modèle Minimaliste */}
                  <div 
                    className={`template-card ${settings.design.documentTemplate === 'minimal-green' ? 'selected' : ''}`}
                    onClick={() => {
                      updateSettings('design', 'documentTemplate', 'minimal-green');
                      updateSettings('design', 'headerStyle', 'minimal');
                      updateSettings('design', 'colorScheme', 'green');
                      updateSettings('design', 'accentColor', '#10b981');
                      updateSettings('design', 'fontFamily', 'roboto');
                    }}
                  >
                    <div className="template-preview minimal-green">
                      <div className="template-header minimal">
                        <div className="template-info">
                          <div className="template-title green">ENTERPRISE NAME</div>
                          <div className="template-subtitle">Business Activity</div>
                        </div>
                      </div>
                      <div className="template-content minimal">
                        <div className="template-line thin"></div>
                        <div className="template-line thin"></div>
                        <div className="template-line thin short"></div>
                      </div>
                    </div>
                    <div className="template-details">
                      <h4>Minimaliste Vert</h4>
                      <p>Design épuré et simple</p>
                    </div>
                  </div>

                  {/* Modèle Corporate */}
                  <div 
                    className={`template-card ${settings.design.documentTemplate === 'corporate-orange' ? 'selected' : ''}`}
                    onClick={() => {
                      updateSettings('design', 'documentTemplate', 'corporate-orange');
                      updateSettings('design', 'headerStyle', 'corporate');
                      updateSettings('design', 'colorScheme', 'orange');
                      updateSettings('design', 'accentColor', '#f59e0b');
                      updateSettings('design', 'fontFamily', 'arial');
                    }}
                  >
                    <div className="template-preview corporate-orange">
                      <div className="template-header corporate">
                        <div className="template-logo orange"></div>
                        <div className="template-info">
                          <div className="template-title orange">ENTERPRISE NAME</div>
                          <div className="template-subtitle">Business Activity</div>
                        </div>
                        <div className="template-contact">
                          <div className="contact-line"></div>
                          <div className="contact-line"></div>
                        </div>
                      </div>
                      <div className="template-content">
                        <div className="template-line"></div>
                        <div className="template-line"></div>
                        <div className="template-line short"></div>
                      </div>
                    </div>
                    <div className="template-details">
                      <h4>Corporate Orange</h4>
                      <p>Style d'entreprise avec informations complètes</p>
                    </div>
                  </div>

                  {/* Modèle Créatif */}
                  <div 
                    className={`template-card ${settings.design.documentTemplate === 'creative-red' ? 'selected' : ''}`}
                    onClick={() => {
                      updateSettings('design', 'documentTemplate', 'creative-red');
                      updateSettings('design', 'headerStyle', 'creative');
                      updateSettings('design', 'colorScheme', 'red');
                      updateSettings('design', 'accentColor', '#ef4444');
                      updateSettings('design', 'fontFamily', 'inter');
                    }}
                  >
                    <div className="template-preview creative-red">
                      <div className="template-header creative">
                        <div className="template-logo red"></div>
                        <div className="template-info angled">
                          <div className="template-title red">ENTERPRISE NAME</div>
                          <div className="template-subtitle">Business Activity</div>
                        </div>
                      </div>
                      <div className="template-content creative">
                        <div className="template-line angled"></div>
                        <div className="template-line angled short"></div>
                        <div className="template-line angled"></div>
                      </div>
                    </div>
                    <div className="template-details">
                      <h4>Créatif Rouge</h4>
                      <p>Design innovant et dynamique</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="design-preview">
                <h3>Aperçu de votre modèle actuel</h3>
                <div className="header-preview" style={{
                  backgroundColor: settings.design.backgroundColor,
                  color: settings.design.textColor,
                  fontFamily: settings.design.fontFamily,
                  border: settings.design.borderStyle === 'clean' ? '1px solid #e5e7eb' : '2px solid #374151'
                }}>
                  <div className="preview-content">
                    {settings.design.showLogo && (
                      <div className={`preview-logo ${settings.design.logoPosition}`}>
                        <div className="logo-placeholder">LOGO</div>
                      </div>
                    )}
                    <div className="preview-company-info">
                      <h4 style={{ color: settings.design.accentColor }}>{settings.company.name}</h4>
                      <p>{settings.company.activity}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-grid">
                {/* Style d'en-tête */}
                <div className="form-group">
                  <label>Style d'en-tête</label>
                  <select
                    value={settings.design.headerStyle}
                    onChange={(e) => updateSettings('design', 'headerStyle', e.target.value)}
                  >
                    <option value="modern">Moderne</option>
                    <option value="classic">Classique</option>
                    <option value="minimal">Minimaliste</option>
                    <option value="corporate">Corporate</option>
                  </select>
                </div>

                {/* Modèle de document */}
                <div className="form-group">
                  <label>Modèle de document</label>
                  <select
                    value={settings.design.documentTemplate}
                    onChange={(e) => updateSettings('design', 'documentTemplate', e.target.value)}
                  >
                    <option value="professional">Professionnel</option>
                    <option value="elegant">Élégant</option>
                    <option value="simple">Simple</option>
                    <option value="creative">Créatif</option>
                  </select>
                </div>

                {/* Schéma de couleurs */}
                <div className="form-group">
                  <label>Schéma de couleurs</label>
                  <select
                    value={settings.design.colorScheme}
                    onChange={(e) => updateSettings('design', 'colorScheme', e.target.value)}
                  >
                    <option value="blue">Bleu</option>
                    <option value="green">Vert</option>
                    <option value="purple">Violet</option>
                    <option value="orange">Orange</option>
                    <option value="red">Rouge</option>
                    <option value="gray">Gris</option>
                  </select>
                </div>

                {/* Police */}
                <div className="form-group">
                  <label>Police de caractères</label>
                  <select
                    value={settings.design.fontFamily}
                    onChange={(e) => updateSettings('design', 'fontFamily', e.target.value)}
                  >
                    <option value="inter">Inter (Moderne)</option>
                    <option value="roboto">Roboto (Lisible)</option>
                    <option value="arial">Arial (Standard)</option>
                    <option value="times">Times (Classique)</option>
                    <option value="helvetica">Helvetica (Élégant)</option>
                  </select>
                </div>

                {/* Couleur d'accent */}
                <div className="form-group">
                  <label>Couleur d'accent</label>
                  <input
                    type="color"
                    value={settings.design.accentColor}
                    onChange={(e) => updateSettings('design', 'accentColor', e.target.value)}
                  />
                </div>

                {/* Couleur de fond */}
                <div className="form-group">
                  <label>Couleur de fond</label>
                  <input
                    type="color"
                    value={settings.design.backgroundColor}
                    onChange={(e) => updateSettings('design', 'backgroundColor', e.target.value)}
                  />
                </div>

                {/* Position du logo */}
                <div className="form-group">
                  <label>Position du logo</label>
                  <select
                    value={settings.design.logoPosition}
                    onChange={(e) => updateSettings('design', 'logoPosition', e.target.value)}
                  >
                    <option value="left">Gauche</option>
                    <option value="center">Centre</option>
                    <option value="right">Droite</option>
                  </select>
                </div>

                {/* Disposition de l'en-tête */}
                <div className="form-group">
                  <label>Disposition de l'en-tête</label>
                  <select
                    value={settings.design.headerLayout}
                    onChange={(e) => updateSettings('design', 'headerLayout', e.target.value)}
                  >
                    <option value="horizontal">Horizontale</option>
                    <option value="vertical">Verticale</option>
                    <option value="split">Divisée</option>
                  </select>
                </div>

                {/* Style de bordure */}
                <div className="form-group">
                  <label>Style de bordure</label>
                  <select
                    value={settings.design.borderStyle}
                    onChange={(e) => updateSettings('design', 'borderStyle', e.target.value)}
                  >
                    <option value="clean">Propre</option>
                    <option value="bold">Gras</option>
                    <option value="none">Aucune</option>
                  </select>
                </div>

                {/* Options d'affichage */}
                <div className="form-group full-width">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={settings.design.showLogo}
                      onChange={(e) => updateSettings('design', 'showLogo', e.target.checked)}
                    />
                    Afficher le logo
                  </label>
                </div>

                <div className="form-group full-width">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={settings.design.showPageNumbers}
                      onChange={(e) => updateSettings('design', 'showPageNumbers', e.target.checked)}
                    />
                    Afficher les numéros de page
                  </label>
                </div>

                <div className="form-group full-width">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={settings.design.showWatermark}
                      onChange={(e) => updateSettings('design', 'showWatermark', e.target.checked)}
                    />
                    Afficher un filigrane
                  </label>
                </div>

                {settings.design.showWatermark && (
                  <div className="form-group full-width">
                    <label>Texte du filigrane</label>
                  <input
                    type="text"
                      value={settings.design.watermarkText}
                      onChange={(e) => updateSettings('design', 'watermarkText', e.target.value)}
                      placeholder="CONFIDENTIEL, BROUILLON, etc."
                    />
                  </div>
                )}

                {/* Style de pied de page */}
                <div className="form-group">
                  <label>Style de pied de page</label>
                  <select
                    value={settings.design.footerStyle}
                    onChange={(e) => updateSettings('design', 'footerStyle', e.target.value)}
                  >
                    <option value="minimal">Minimal</option>
                    <option value="detailed">Détaillé</option>
                    <option value="none">Aucun</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Tax Settings */}
          {activeTab === 'taxes' && (
            <div className="settings-section">
              <h2 className="section-title">Configuration des taxes</h2>
              
              <div className="tax-info">
                <p>Configurez les taux de taxes applicables en Algérie</p>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>TVA (%) *</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={settings.taxes.tva}
                    onChange={(e) => updateSettings('taxes', 'tva', parseFloat(e.target.value))}
                  />
                  <small>Taxe sur la Valeur Ajoutée (Standard: 19%)</small>
                </div>

                <div className="form-group">
                  <label>TAP (%) *</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={settings.taxes.tap}
                    onChange={(e) => updateSettings('taxes', 'tap', parseFloat(e.target.value))}
                  />
                  <small>Taxe sur l'Activité Professionnelle (Standard: 2%)</small>
                </div>

                <div className="form-group">
                  <label>VF (%) *</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={settings.taxes.vf}
                    onChange={(e) => updateSettings('taxes', 'vf', parseFloat(e.target.value))}
                  />
                  <small>Versement Forfaitaire (Standard: 1%)</small>
                </div>

                <div className="form-group full-width">
                  <label>Taxes par défaut</label>
                  <div className="checkbox-group">
                    {['tva', 'tap', 'vf'].map(tax => (
                      <label key={tax} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={settings.taxes.defaultTaxes.includes(tax)}
                          onChange={(e) => {
                            const newTaxes = e.target.checked
                              ? [...settings.taxes.defaultTaxes, tax]
                              : settings.taxes.defaultTaxes.filter(t => t !== tax);
                            updateSettings('taxes', 'defaultTaxes', newTaxes);
                          }}
                        />
                        {tax.toUpperCase()}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group full-width">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={settings.taxes.taxIncluded}
                      onChange={(e) => updateSettings('taxes', 'taxIncluded', e.target.checked)}
                    />
                    Prix TTC par défaut
                  </label>
                  <small>Cochez si vos prix incluent les taxes par défaut</small>
                </div>
              </div>
            </div>
          )}

          {/* Documents Settings */}
          {activeTab === 'documents' && (
            <div className="settings-section">
              <h2 className="section-title">{t('settings.documents.title')}</h2>
              <p className="section-description">{t('settings.documents.description')}</p>
              
              <div className="form-grid">
                {/* Paramètres généraux */}
                <div className="form-group">
                  <label>{t('settings.documents.currency')}</label>
                  <select
                    value={settings.invoicing.currency}
                    onChange={(e) => updateSettings('invoicing', 'currency', e.target.value)}
                  >
                    <option value="DZD">Dinar Algérien (DZD)</option>
                    <option value="EUR">Euro (EUR)</option>
                    <option value="USD">Dollar US (USD)</option>
                  </select>
                </div>

                <div className="form-group language-setting-highlight">
                  <label>{t('settings.documents.language')}</label>
                  <select
                    value={settings.invoicing.language}
                    onChange={(e) => updateSettings('invoicing', 'language', e.target.value)}
                  >
                    <option value="fr">Français</option>
                    <option value="ar">العربية</option>
                    <option value="en">English</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('settings.documents.date_format')}</label>
                  <select
                    value={settings.invoicing.dateFormat}
                    onChange={(e) => updateSettings('invoicing', 'dateFormat', e.target.value)}
                  >
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Délai de paiement par défaut (jours)</label>
                  <input
                    type="number"
                    min="0"
                    value={settings.invoicing.paymentTerms}
                    onChange={(e) => updateSettings('invoicing', 'paymentTerms', parseInt(e.target.value))}
                  />
                </div>

                {/* Section Préfixes - 68 Documents */}
                <div className="form-group full-width">
                  <h3 className="subsection-title">Préfixes des 68 types de documents</h3>
                  <p className="numbering-description">Configurez les préfixes pour tous vos types de documents</p>
                </div>

                {/* FACTURATION */}
                <div className="numbering-category">
                  <h4 className="category-title">FACTURATION (3 documents)</h4>
                  <div className="numbering-grid-prefix">
                    <div className="numbering-item-prefix">
                      <label>Facture standard</label>
                      <input type="text" value={settings.invoicing.invoicePrefix || 'FAC'} onChange={(e) => updateSettings('invoicing', 'invoicePrefix', e.target.value)} placeholder="FAC" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Facture proforma</label>
                      <input type="text" value={settings.invoicing.proformaPrefix || 'PRO'} onChange={(e) => updateSettings('invoicing', 'proformaPrefix', e.target.value)} placeholder="PRO" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Demande de devis</label>
                      <input type="text" value={settings.invoicing.quoteRequestPrefix || 'DDV'} onChange={(e) => updateSettings('invoicing', 'quoteRequestPrefix', e.target.value)} placeholder="DDV" />
                    </div>
                  </div>
                </div>

                {/* OFFRES COMMERCIALES */}
                <div className="numbering-category">
                  <h4 className="category-title">OFFRES COMMERCIALES (3 documents)</h4>
                  <div className="numbering-grid-prefix">
                    <div className="numbering-item-prefix">
                      <label>Devis</label>
                      <input type="text" value={settings.invoicing.quotePrefix || 'DEV'} onChange={(e) => updateSettings('invoicing', 'quotePrefix', e.target.value)} placeholder="DEV" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Bon de commande</label>
                      <input type="text" value={settings.invoicing.orderPrefix || 'BC'} onChange={(e) => updateSettings('invoicing', 'orderPrefix', e.target.value)} placeholder="BC" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Attachement</label>
                      <input type="text" value={settings.invoicing.attachmentPrefix || 'ATT'} onChange={(e) => updateSettings('invoicing', 'attachmentPrefix', e.target.value)} placeholder="ATT" />
                    </div>
                  </div>
                </div>

                {/* LOGISTIQUE */}
                <div className="numbering-category">
                  <h4 className="category-title">LOGISTIQUE (6 documents)</h4>
                  <div className="numbering-grid-prefix">
                    <div className="numbering-item-prefix">
                      <label>Bon de livraison</label>
                      <input type="text" value={settings.invoicing.deliveryPrefix || 'BL'} onChange={(e) => updateSettings('invoicing', 'deliveryPrefix', e.target.value)} placeholder="BL" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Bordereau d'envoi</label>
                      <input type="text" value={settings.invoicing.shippingPrefix || 'BE'} onChange={(e) => updateSettings('invoicing', 'shippingPrefix', e.target.value)} placeholder="BE" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Bon de réception</label>
                      <input type="text" value={settings.invoicing.receptionPrefix || 'BR'} onChange={(e) => updateSettings('invoicing', 'receptionPrefix', e.target.value)} placeholder="BR" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Bordereau de retour</label>
                      <input type="text" value={settings.invoicing.returnSlipPrefix || 'BRT'} onChange={(e) => updateSettings('invoicing', 'returnSlipPrefix', e.target.value)} placeholder="BRT" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Fiche de stock</label>
                      <input type="text" value={settings.invoicing.stockCardPrefix || 'FS'} onChange={(e) => updateSettings('invoicing', 'stockCardPrefix', e.target.value)} placeholder="FS" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Bon de sortie matériel</label>
                      <input type="text" value={settings.invoicing.materialExitPrefix || 'BSM'} onChange={(e) => updateSettings('invoicing', 'materialExitPrefix', e.target.value)} placeholder="BSM" />
                    </div>
                  </div>
                </div>

                {/* COMPTABILITÉ & FINANCE */}
                <div className="numbering-category">
                  <h4 className="category-title">COMPTABILITÉ & FINANCE (3 documents)</h4>
                  <div className="numbering-grid-prefix">
                    <div className="numbering-item-prefix">
                      <label>Avoir</label>
                      <input type="text" value={settings.invoicing.creditNotePrefix || 'AVO'} onChange={(e) => updateSettings('invoicing', 'creditNotePrefix', e.target.value)} placeholder="AVO" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Relevé de compte</label>
                      <input type="text" value={settings.invoicing.statementPrefix || 'RLC'} onChange={(e) => updateSettings('invoicing', 'statementPrefix', e.target.value)} placeholder="RLC" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Facture d'acompte</label>
                      <input type="text" value={settings.invoicing.advancePrefix || 'ACO'} onChange={(e) => updateSettings('invoicing', 'advancePrefix', e.target.value)} placeholder="ACO" />
                    </div>
                  </div>
                </div>

                {/* DOCUMENTS CONTRACTUELS */}
                <div className="numbering-category">
                  <h4 className="category-title">DOCUMENTS CONTRACTUELS (3 documents)</h4>
                  <div className="numbering-grid-prefix">
                    <div className="numbering-item-prefix">
                      <label>Contrat commercial</label>
                      <input type="text" value={settings.invoicing.contractPrefix || 'CC'} onChange={(e) => updateSettings('invoicing', 'contractPrefix', e.target.value)} placeholder="CC" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Convention de service</label>
                      <input type="text" value={settings.invoicing.serviceAgreementPrefix || 'CS'} onChange={(e) => updateSettings('invoicing', 'serviceAgreementPrefix', e.target.value)} placeholder="CS" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Certificat de conformité</label>
                      <input type="text" value={settings.invoicing.compliancePrefix || 'CERT'} onChange={(e) => updateSettings('invoicing', 'compliancePrefix', e.target.value)} placeholder="CERT" />
                    </div>
                  </div>
                </div>

                {/* PAIEMENTS & RECOUVREMENT */}
                <div className="numbering-category">
                  <h4 className="category-title">PAIEMENTS & RECOUVREMENT (3 documents)</h4>
                  <div className="numbering-grid-prefix">
                    <div className="numbering-item-prefix">
                      <label>Mise en demeure</label>
                      <input type="text" value={settings.invoicing.demandPrefix || 'MD'} onChange={(e) => updateSettings('invoicing', 'demandPrefix', e.target.value)} placeholder="MD" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Reçu de paiement</label>
                      <input type="text" value={settings.invoicing.receiptPrefix || 'REC'} onChange={(e) => updateSettings('invoicing', 'receiptPrefix', e.target.value)} placeholder="REC" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Échéancier</label>
                      <input type="text" value={settings.invoicing.schedulePrefix || 'ECH'} onChange={(e) => updateSettings('invoicing', 'schedulePrefix', e.target.value)} placeholder="ECH" />
                    </div>
                  </div>
                </div>

                {/* PROJETS & TRAVAUX */}
                <div className="numbering-category">
                  <h4 className="category-title">PROJETS & TRAVAUX (3 documents)</h4>
                  <div className="numbering-grid-prefix">
                    <div className="numbering-item-prefix">
                      <label>Procès-verbal de réception</label>
                      <input type="text" value={settings.invoicing.receptionReportPrefix || 'PVR'} onChange={(e) => updateSettings('invoicing', 'receptionReportPrefix', e.target.value)} placeholder="PVR" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Rapport d'intervention</label>
                      <input type="text" value={settings.invoicing.interventionPrefix || 'RI'} onChange={(e) => updateSettings('invoicing', 'interventionPrefix', e.target.value)} placeholder="RI" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Fiche de non-conformité</label>
                      <input type="text" value={settings.invoicing.nonCompliancePrefix || 'FNC'} onChange={(e) => updateSettings('invoicing', 'nonCompliancePrefix', e.target.value)} placeholder="FNC" />
                    </div>
                  </div>
                </div>

                {/* DOCUMENTS LÉGAUX */}
                <div className="numbering-category">
                  <h4 className="category-title">DOCUMENTS LÉGAUX (3 documents)</h4>
                  <div className="numbering-grid-prefix">
                    <div className="numbering-item-prefix">
                      <label>Attestation fiscale</label>
                      <input type="text" value={settings.invoicing.taxCertificatePrefix || 'AF'} onChange={(e) => updateSettings('invoicing', 'taxCertificatePrefix', e.target.value)} placeholder="AF" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Certificat de travail effectué</label>
                      <input type="text" value={settings.invoicing.workCertificatePrefix || 'CTE'} onChange={(e) => updateSettings('invoicing', 'workCertificatePrefix', e.target.value)} placeholder="CTE" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Notification légale</label>
                      <input type="text" value={settings.invoicing.legalNoticePrefix || 'NL'} onChange={(e) => updateSettings('invoicing', 'legalNoticePrefix', e.target.value)} placeholder="NL" />
                    </div>
                  </div>
                </div>

                {/* RESSOURCES HUMAINES */}
                <div className="numbering-category">
                  <h4 className="category-title">RESSOURCES HUMAINES (4 documents)</h4>
                  <div className="numbering-grid-prefix">
                    <div className="numbering-item-prefix">
                      <label>Fiche de paie</label>
                      <input type="text" value={settings.invoicing.payslipPrefix || 'FP'} onChange={(e) => updateSettings('invoicing', 'payslipPrefix', e.target.value)} placeholder="FP" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Contrat de travail</label>
                      <input type="text" value={settings.invoicing.workContractPrefix || 'CT'} onChange={(e) => updateSettings('invoicing', 'workContractPrefix', e.target.value)} placeholder="CT" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Attestation de travail</label>
                      <input type="text" value={settings.invoicing.workAttestationPrefix || 'AT'} onChange={(e) => updateSettings('invoicing', 'workAttestationPrefix', e.target.value)} placeholder="AT" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Demande de congé</label>
                      <input type="text" value={settings.invoicing.leaveRequestPrefix || 'DC'} onChange={(e) => updateSettings('invoicing', 'leaveRequestPrefix', e.target.value)} placeholder="DC" />
                    </div>
                  </div>
                </div>

                {/* PRODUCTION & QUALITÉ */}
                <div className="numbering-category">
                  <h4 className="category-title">PRODUCTION & QUALITÉ (4 documents)</h4>
                  <div className="numbering-grid-prefix">
                    <div className="numbering-item-prefix">
                      <label>Fiche de production</label>
                      <input type="text" value={settings.invoicing.productionSheetPrefix || 'FPR'} onChange={(e) => updateSettings('invoicing', 'productionSheetPrefix', e.target.value)} placeholder="FPR" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Contrôle qualité</label>
                      <input type="text" value={settings.invoicing.qualityControlPrefix || 'CQ'} onChange={(e) => updateSettings('invoicing', 'qualityControlPrefix', e.target.value)} placeholder="CQ" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Bon de fabrication</label>
                      <input type="text" value={settings.invoicing.manufacturingOrderPrefix || 'BF'} onChange={(e) => updateSettings('invoicing', 'manufacturingOrderPrefix', e.target.value)} placeholder="BF" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Rapport de défaillance</label>
                      <input type="text" value={settings.invoicing.failureReportPrefix || 'RD'} onChange={(e) => updateSettings('invoicing', 'failureReportPrefix', e.target.value)} placeholder="RD" />
                    </div>
                  </div>
                </div>

                {/* TRANSPORT & LIVRAISON */}
                <div className="numbering-category">
                  <h4 className="category-title">TRANSPORT & LIVRAISON (3 documents)</h4>
                  <div className="numbering-grid-prefix">
                    <div className="numbering-item-prefix">
                      <label>Feuille de route</label>
                      <input type="text" value={settings.invoicing.routeSheetPrefix || 'FR'} onChange={(e) => updateSettings('invoicing', 'routeSheetPrefix', e.target.value)} placeholder="FR" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Rapport de livraison</label>
                      <input type="text" value={settings.invoicing.deliveryReportPrefix || 'RL'} onChange={(e) => updateSettings('invoicing', 'deliveryReportPrefix', e.target.value)} placeholder="RL" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Bon de transport</label>
                      <input type="text" value={settings.invoicing.transportOrderPrefix || 'BT'} onChange={(e) => updateSettings('invoicing', 'transportOrderPrefix', e.target.value)} placeholder="BT" />
                    </div>
                  </div>
                </div>

                {/* IMMOBILIER & LOCATION */}
                <div className="numbering-category">
                  <h4 className="category-title">IMMOBILIER & LOCATION (3 documents)</h4>
                  <div className="numbering-grid-prefix">
                    <div className="numbering-item-prefix">
                      <label>Contrat de location</label>
                      <input type="text" value={settings.invoicing.rentalContractPrefix || 'CL'} onChange={(e) => updateSettings('invoicing', 'rentalContractPrefix', e.target.value)} placeholder="CL" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>État des lieux</label>
                      <input type="text" value={settings.invoicing.propertyInspectionPrefix || 'EDL'} onChange={(e) => updateSettings('invoicing', 'propertyInspectionPrefix', e.target.value)} placeholder="EDL" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Quittance de loyer</label>
                      <input type="text" value={settings.invoicing.rentReceiptPrefix || 'QL'} onChange={(e) => updateSettings('invoicing', 'rentReceiptPrefix', e.target.value)} placeholder="QL" />
                    </div>
                  </div>
                </div>

                {/* SÉCURITÉ & ASSURANCE */}
                <div className="numbering-category">
                  <h4 className="category-title">SÉCURITÉ & ASSURANCE (3 documents)</h4>
                  <div className="numbering-grid-prefix">
                    <div className="numbering-item-prefix">
                      <label>Déclaration d'accident</label>
                      <input type="text" value={settings.invoicing.accidentReportPrefix || 'DA'} onChange={(e) => updateSettings('invoicing', 'accidentReportPrefix', e.target.value)} placeholder="DA" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Rapport de sécurité</label>
                      <input type="text" value={settings.invoicing.securityReportPrefix || 'RS'} onChange={(e) => updateSettings('invoicing', 'securityReportPrefix', e.target.value)} placeholder="RS" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Fiche conformité sécurité</label>
                      <input type="text" value={settings.invoicing.safetyCompliancePrefix || 'FCS'} onChange={(e) => updateSettings('invoicing', 'safetyCompliancePrefix', e.target.value)} placeholder="FCS" />
                    </div>
                  </div>
                </div>

                {/* SERVICES & MAINTENANCE */}
                <div className="numbering-category">
                  <h4 className="category-title">SERVICES & MAINTENANCE (4 documents)</h4>
                  <div className="numbering-grid-prefix">
                    <div className="numbering-item-prefix">
                      <label>Rapport de maintenance</label>
                      <input type="text" value={settings.invoicing.maintenanceReportPrefix || 'RM'} onChange={(e) => updateSettings('invoicing', 'maintenanceReportPrefix', e.target.value)} placeholder="RM" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Planification maintenance</label>
                      <input type="text" value={settings.invoicing.maintenancePlanPrefix || 'PM'} onChange={(e) => updateSettings('invoicing', 'maintenancePlanPrefix', e.target.value)} placeholder="PM" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Fiche intervention client</label>
                      <input type="text" value={settings.invoicing.clientInterventionPrefix || 'FIC'} onChange={(e) => updateSettings('invoicing', 'clientInterventionPrefix', e.target.value)} placeholder="FIC" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Garantie produit</label>
                      <input type="text" value={settings.invoicing.warrantyPrefix || 'GAR'} onChange={(e) => updateSettings('invoicing', 'warrantyPrefix', e.target.value)} placeholder="GAR" />
                    </div>
                  </div>
                </div>

                {/* INFORMATIQUE & TÉLÉCOMS */}
                <div className="numbering-category">
                  <h4 className="category-title">INFORMATIQUE & TÉLÉCOMS (4 documents)</h4>
                  <div className="numbering-grid-prefix">
                    <div className="numbering-item-prefix">
                      <label>Rapport audit IT</label>
                      <input type="text" value={settings.invoicing.itAuditPrefix || 'RAI'} onChange={(e) => updateSettings('invoicing', 'itAuditPrefix', e.target.value)} placeholder="RAI" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Fiche installation logiciel</label>
                      <input type="text" value={settings.invoicing.softwareInstallPrefix || 'FIL'} onChange={(e) => updateSettings('invoicing', 'softwareInstallPrefix', e.target.value)} placeholder="FIL" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Contrat maintenance IT</label>
                      <input type="text" value={settings.invoicing.itMaintenancePrefix || 'CMIT'} onChange={(e) => updateSettings('invoicing', 'itMaintenancePrefix', e.target.value)} placeholder="CMIT" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Sauvegarde données</label>
                      <input type="text" value={settings.invoicing.backupPrefix || 'BAK'} onChange={(e) => updateSettings('invoicing', 'backupPrefix', e.target.value)} placeholder="BAK" />
                    </div>
                  </div>
                </div>

                {/* ACHATS & APPROVISIONNEMENT */}
                <div className="numbering-category">
                  <h4 className="category-title">ACHATS & APPROVISIONNEMENT (4 documents)</h4>
                  <div className="numbering-grid-prefix">
                    <div className="numbering-item-prefix">
                      <label>Demande d'achat</label>
                      <input type="text" value={settings.invoicing.purchaseRequestPrefix || 'DA'} onChange={(e) => updateSettings('invoicing', 'purchaseRequestPrefix', e.target.value)} placeholder="DA" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Appel d'offres</label>
                      <input type="text" value={settings.invoicing.tenderPrefix || 'AO'} onChange={(e) => updateSettings('invoicing', 'tenderPrefix', e.target.value)} placeholder="AO" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Comparatif fournisseurs</label>
                      <input type="text" value={settings.invoicing.supplierComparisonPrefix || 'CF'} onChange={(e) => updateSettings('invoicing', 'supplierComparisonPrefix', e.target.value)} placeholder="CF" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Réception marchandises</label>
                      <input type="text" value={settings.invoicing.goodsReceiptPrefix || 'RM'} onChange={(e) => updateSettings('invoicing', 'goodsReceiptPrefix', e.target.value)} placeholder="RM" />
                    </div>
                  </div>
                </div>

                {/* FORMATION & DÉVELOPPEMENT */}
                <div className="numbering-category">
                  <h4 className="category-title">FORMATION & DÉVELOPPEMENT (3 documents)</h4>
                  <div className="numbering-grid-prefix">
                    <div className="numbering-item-prefix">
                      <label>Plan de formation</label>
                      <input type="text" value={settings.invoicing.trainingPlanPrefix || 'PF'} onChange={(e) => updateSettings('invoicing', 'trainingPlanPrefix', e.target.value)} placeholder="PF" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Évaluation formation</label>
                      <input type="text" value={settings.invoicing.trainingEvalPrefix || 'EF'} onChange={(e) => updateSettings('invoicing', 'trainingEvalPrefix', e.target.value)} placeholder="EF" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Certificat de formation</label>
                      <input type="text" value={settings.invoicing.trainingCertPrefix || 'CF'} onChange={(e) => updateSettings('invoicing', 'trainingCertPrefix', e.target.value)} placeholder="CF" />
                    </div>
                  </div>
                </div>

                {/* COMMERCE & VENTE */}
                <div className="numbering-category">
                  <h4 className="category-title">COMMERCE & VENTE (3 documents)</h4>
                  <div className="numbering-grid-prefix">
                    <div className="numbering-item-prefix">
                      <label>Fiche prospect</label>
                      <input type="text" value={settings.invoicing.prospectSheetPrefix || 'FP'} onChange={(e) => updateSettings('invoicing', 'prospectSheetPrefix', e.target.value)} placeholder="FP" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Rapport visite client</label>
                      <input type="text" value={settings.invoicing.clientVisitPrefix || 'RVC'} onChange={(e) => updateSettings('invoicing', 'clientVisitPrefix', e.target.value)} placeholder="RVC" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Tarifs et catalogues</label>
                      <input type="text" value={settings.invoicing.catalogPrefix || 'CAT'} onChange={(e) => updateSettings('invoicing', 'catalogPrefix', e.target.value)} placeholder="CAT" />
                    </div>
                  </div>
                </div>

                {/* ÉQUIPEMENTS & MATÉRIEL */}
                <div className="numbering-category">
                  <h4 className="category-title">ÉQUIPEMENTS & MATÉRIEL (3 documents)</h4>
                  <div className="numbering-grid-prefix">
                    <div className="numbering-item-prefix">
                      <label>Fiche équipement</label>
                      <input type="text" value={settings.invoicing.equipmentSheetPrefix || 'FE'} onChange={(e) => updateSettings('invoicing', 'equipmentSheetPrefix', e.target.value)} placeholder="FE" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Calibrage instruments</label>
                      <input type="text" value={settings.invoicing.calibrationPrefix || 'CAL'} onChange={(e) => updateSettings('invoicing', 'calibrationPrefix', e.target.value)} placeholder="CAL" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Prêt de matériel</label>
                      <input type="text" value={settings.invoicing.materialLoanPrefix || 'PM'} onChange={(e) => updateSettings('invoicing', 'materialLoanPrefix', e.target.value)} placeholder="PM" />
                    </div>
                  </div>
                </div>

                {/* ANALYSE & REPORTING */}
                <div className="numbering-category">
                  <h4 className="category-title">ANALYSE & REPORTING (4 documents)</h4>
                  <div className="numbering-grid-prefix">
                    <div className="numbering-item-prefix">
                      <label>Tableau de bord</label>
                      <input type="text" value={settings.invoicing.dashboardPrefix || 'TDB'} onChange={(e) => updateSettings('invoicing', 'dashboardPrefix', e.target.value)} placeholder="TDB" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Rapport financier</label>
                      <input type="text" value={settings.invoicing.financialReportPrefix || 'RF'} onChange={(e) => updateSettings('invoicing', 'financialReportPrefix', e.target.value)} placeholder="RF" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Étude de marché</label>
                      <input type="text" value={settings.invoicing.marketStudyPrefix || 'EM'} onChange={(e) => updateSettings('invoicing', 'marketStudyPrefix', e.target.value)} placeholder="EM" />
                    </div>
                    <div className="numbering-item-prefix">
                      <label>Benchmark concurrentiel</label>
                      <input type="text" value={settings.invoicing.benchmarkPrefix || 'BEN'} onChange={(e) => updateSettings('invoicing', 'benchmarkPrefix', e.target.value)} placeholder="BEN" />
                    </div>
                  </div>
                </div>



                {/* Section Options avancées */}
                <div className="form-group full-width">
                  <h3 className="subsection-title">Options avancées</h3>
                </div>

                <div className="form-group">
                  <label>Taux pénalités de retard (%)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={settings.invoicing.lateFeesRate}
                    onChange={(e) => updateSettings('invoicing', 'lateFeesRate', parseFloat(e.target.value))}
                  />
                </div>

                <div className="form-group">
                  <label>Validité par défaut (jours)</label>
                  <input
                    type="number"
                    min="1"
                    value={settings.invoicing.defaultValidity || 30}
                    onChange={(e) => updateSettings('invoicing', 'defaultValidity', parseInt(e.target.value))}
                    placeholder="30"
                  />
                </div>

                <div className="form-group full-width">
                  <label className="checkbox-label">
                  <input
                      type="checkbox"
                      checked={settings.invoicing.autoArchive || false}
                      onChange={(e) => updateSettings('invoicing', 'autoArchive', e.target.checked)}
                    />
                    Archivage automatique des documents anciens
                  </label>
                  <small>Archive automatiquement les documents de plus de 12 mois</small>
                </div>

                <div className="form-group full-width">
                  <label className="checkbox-label">
                  <input
                      type="checkbox"
                      checked={settings.invoicing.sequentialNumbering || true}
                      onChange={(e) => updateSettings('invoicing', 'sequentialNumbering', e.target.checked)}
                    />
                    Numérotation séquentielle obligatoire
                  </label>
                  <small>Force la numérotation séquentielle pour la conformité légale</small>
                </div>
              </div>
            </div>
          )}

          {/* Notifications Settings */}
          {activeTab === 'notifications' && (
            <div className="settings-section">
              <h2 className="section-title">Préférences de notifications</h2>
              
              <div className="notifications-grid">
                <div className="notification-item">
                  <div className="notification-info">
                    <h3>Notifications par email</h3>
                    <p>Recevoir les notifications importantes par email</p>
                  </div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={settings.notifications.emailNotifications}
                      onChange={(e) => updateSettings('notifications', 'emailNotifications', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="notification-item">
                  <div className="notification-info">
                    <h3>Rappels de factures</h3>
                    <p>Recevoir des rappels pour les factures impayées</p>
                  </div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={settings.notifications.invoiceReminders}
                      onChange={(e) => updateSettings('notifications', 'invoiceReminders', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="notification-item">
                  <div className="notification-info">
                    <h3>Alertes stock faible</h3>
                    <p>Être alerté quand le stock d'un produit est bas</p>
                  </div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={settings.notifications.lowStockAlerts}
                      onChange={(e) => updateSettings('notifications', 'lowStockAlerts', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="notification-item">
                  <div className="notification-info">
                    <h3>Alertes de paiement</h3>
                    <p>Notifications pour les paiements reçus</p>
                  </div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={settings.notifications.paymentAlerts}
                      onChange={(e) => updateSettings('notifications', 'paymentAlerts', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="settings-actions">
            {unsavedChanges && (
              <div className="unsaved-notice">
                <span>⚠️ Vous avez des modifications non sauvegardées</span>
              </div>
            )}
            <button 
              className={`btn btn-primary ${unsavedChanges ? 'pulse' : ''}`}
              onClick={handleSave}
            >
              <Save size={20} />
              Sauvegarder les paramètres
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;